import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Stack, TextInput, Button } from '@mantine/core';
import { IconArrowLeft, IconMail } from '@tabler/icons-react';
import { useAuthService } from '@actschurch/ccms-sdk';
import { PageIntro } from '@/components/rides/PageIntro';
import { Callout } from '@/components/ui';
import { useCopy } from '@/lib/settings';

export default function ForgotPasswordPage() {
  const authService = useAuthService();
  const { t } = useCopy();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError('Please enter your email address, for example name@gmail.com.');
      return;
    }
    setSubmitting(true);
    try {
      await authService.forgotPassword({ email: email.trim(), appUrl: window.location.origin });
      setSent(true);
    } catch {
      setError("We couldn't send the link just now. Please check your internet connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rides-main">
      <PageIntro title={sent ? 'Check your email' : 'Reset your password'} lede={sent ? undefined : t('forgot.intro')} />
      <div className="rides-card" style={{ padding: 18 }}>
        {sent ? (
          <Stack gap="md">
            <Callout tone="ok" live icon={<IconMail size={20} />}>{t('forgot.sent')}</Callout>
            <Button component={Link} to="/auth/login" fullWidth>Back to sign in</Button>
            <Button variant="subtle" onClick={() => setSent(false)} fullWidth>Use a different email address</Button>
          </Stack>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <Stack gap="md">
              <TextInput
                label="Email address"
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.currentTarget.value); setError(null); }}
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                spellCheck={false}
                error={error}
              />
              <Button type="submit" loading={submitting} leftSection={<IconMail size={20} />} fullWidth>Send reset link</Button>
              <Button component={Link} to="/auth/login" variant="subtle" leftSection={<IconArrowLeft size={18} />} fullWidth>Back to sign in</Button>
            </Stack>
          </form>
        )}
      </div>
    </div>
  );
}
